import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeviceHub as DeviceIcon,
  Sensors as SensorIcon,
  Battery3Bar as BatteryIcon,
  SignalWifi4Bar as SignalIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "@hooks/redux";
import {
  fetchDevices,
  createDevice,
  updateDevice,
  markForDeletion,
  unmarkForDeletion,
  batchSaveDevices,
  setDeviceEdited,
} from "@store/slices/devicesSlice";
import { Device } from "@types";
import { format, parseISO } from "date-fns";
import SaveToolbar from "@components/common/SaveToolbar";
import UnsavedChangesDialog from "@components/common/UnsavedChangesDialog";
import { useUnsavedChanges } from "@hooks/useUnsavedChanges";
import DeviceDialog from "@components/devices/DeviceDialog";

const Devices: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    devices,
    loadingState,
    error,
    pendingDeletions,
  } = useAppSelector((state) => state.devices);

  // Get apiaries for hive assignment
  const { apiaries } = useAppSelector((state) => state.locations);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedDevice, setSelectedDeviceLocal] = useState<Device | null>(
    null,
  );
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showLoadingIcon, setShowLoadingIcon] = useState(false);

  // Navigation guard
  const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    {
      when: pendingDeletions.length > 0 && !showLoadingIcon,
      message: "You have unsaved changes. Are you sure you want to leave?",
      onConfirm: () => {
        dispatch(setDeviceEdited(false));
      },
    },
  );

  useEffect(() => {
    if (loadingState === "idle") {
      dispatch(fetchDevices());
    }
  }, [dispatch, loadingState]);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    device: Device,
  ) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedDeviceLocal(device);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleAddDevice = () => {
    setShowAddDialog(true);
  };

  const handleEditDevice = () => {
    if (selectedDevice) {
      setShowAddDialog(true);
    }
    setMenuAnchor(null);
    // Note: Don't set selectedDevice to null here, it's needed for the dialog
  };

  const handleMarkForDeletion = (deviceId: number) => {
    if (pendingDeletions.includes(deviceId)) {
      dispatch(unmarkForDeletion(deviceId));
    } else {
      dispatch(markForDeletion(deviceId));
    }
    handleMenuClose();
  };

  const handleBatchSave = async () => {
    if (pendingDeletions.length === 0) return;

    if (
      window.confirm(
        `Are you sure you want to delete ${pendingDeletions.length} device(s)? This action cannot be undone.`,
      )
    ) {
      setShowLoadingIcon(true);
      try {
        await dispatch(batchSaveDevices({ deletions: pendingDeletions }));
      } finally {
        setShowLoadingIcon(false);
      }
    }
  };

  const handleSaveDevice = async (deviceData: Partial<Device>) => {
    setShowLoadingIcon(true);
    try {
      let result;
      if (selectedDevice) {
        result = await dispatch(updateDevice({ id: selectedDevice.id, data: deviceData }));
      } else {
        result = await dispatch(createDevice(deviceData));
      }

      if (createDevice.fulfilled.match(result) || updateDevice.fulfilled.match(result as any)) {
        setShowAddDialog(false);
        setSelectedDeviceLocal(null);
      }
    } finally {
      setShowLoadingIcon(false);
    }
  };

  const getBatteryLevel = (voltage?: number): string => {
    if (!voltage) return "Unknown";
    if (voltage > 3.7) return "Good";
    if (voltage > 3.5) return "Fair";
    return "Low";
  };

  const getBatteryColor = (voltage?: number) => {
    if (!voltage) return "default";
    if (voltage > 3.7) return "success";
    if (voltage > 3.5) return "warning";
    return "error";
  };

  const getConnectionStatus = (device: Device): string => {
    if (device.online) return "Online";

    const lastSeen = device.last_message_received || device.last_seen;
    if (!lastSeen) return "Never connected";

    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes =
      (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);

    if (diffMinutes < 60) return "Recent";
    if (diffMinutes < 1440) return "Offline";
    return "Disconnected";
  };

  const getConnectionColor = (device: Device) => {
    const status = getConnectionStatus(device);
    switch (status) {
      case "Online":
        return "success";
      case "Recent":
        return "warning";
      case "Offline":
        return "error";
      default:
        return "default";
    }
  };

  if (loadingState === "pending") {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ py: { xs: 1.5, md: 3 }, px: { xs: 1.5, md: 3 } }}>
      {/* Save Toolbar */}
      <SaveToolbar
        onSave={handleBatchSave}
        saveDisabled={pendingDeletions.length === 0 || showLoadingIcon}
        loading={showLoadingIcon}
        deleteMode={pendingDeletions.length > 0}
        showDelete={false}
        saveText={
          pendingDeletions.length > 0
            ? `Save & Delete (${pendingDeletions.length})`
            : "Save"
        }
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: "space-between",
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: { xs: 2, md: 3 },
          mt: { xs: 1, md: 2 },
          gap: { xs: 1.5, sm: 0 }
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, mb: { xs: 0.5, md: 1 } }}>
            Devices
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body1" color="textSecondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
              Manage your SmartNyuki Base devices
            </Typography>
            <IconButton size="small" onClick={() => setShowInfo(!showInfo)}>
              <InfoIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />
            </IconButton>
          </Box>
        </Box>

        <Button
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<AddIcon />}
          onClick={handleAddDevice}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Configure Device
        </Button>
      </Box>

      {/* Info Text */}
      {showInfo && (
        <Alert severity="info" sx={{ mb: { xs: 2, md: 3 }, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
            Connect your SmartNyuki Base devices to monitor hive conditions in
            real-time. Visit{" "}
            <a
              href="https://smartnyuki.jhubafrica.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              smartnyuki.jhubafrica.com
            </a>{" "}
            for setup instructions.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, md: 3 } }}>
          {error}
        </Alert>
      )}

      {/* Pending Deletions Warning */}
      {pendingDeletions.length > 0 && (
        <Alert severity="warning" sx={{ mb: { xs: 2, md: 3 }, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
          {pendingDeletions.length} device(s) marked for deletion. Click "Save &
          Delete" to confirm.
        </Alert>
      )}

      {/* Devices Grid */}
      {devices.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: { xs: 4, md: 6 }, px: { xs: 2, md: 3 } }}>
            <DeviceIcon sx={{ fontSize: { xs: 48, md: 64 }, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              No devices connected yet
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
              Connect your first SmartNyuki Base device to start monitoring your
              hives.
            </Typography>
            <Box sx={{ textAlign: "center" }}>
              <img
                src="/img/SmartNyuki-base-small.png"
                alt="SmartNyuki Base Device"
                style={{ maxWidth: "100%", width: 500 }}
              />
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {devices.map((device) => (
            <Grid item xs={12} sm={6} md={4} key={device.id}>
              <Card
                onClick={() => navigate(`/devices/${device.id}`)}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: pendingDeletions.includes(device.id)
                    ? "2px solid"
                    : "none",
                  borderColor: pendingDeletions.includes(device.id)
                    ? "error.main"
                    : "transparent",
                  opacity: pendingDeletions.includes(device.id) ? 0.6 : 1,
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="h6" component="h3" noWrap>
                        {device.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {device.key}
                      </Typography>
                    </Box>

                    {device.owner && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, device)}
                      >
                        <MoreIcon />
                      </IconButton>
                    )}
                  </Box>

                  {/* Status Chips */}
                  <Box
                    sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}
                  >
                    <Chip
                      icon={<SignalIcon />}
                      label={getConnectionStatus(device)}
                      size="small"
                      color={
                        getConnectionColor(device) as any
                      }
                      variant="outlined"
                    />

                    {device.battery_voltage && (
                      <Chip
                        icon={<BatteryIcon />}
                        label={`${getBatteryLevel(device.battery_voltage)} (${device.battery_voltage.toFixed(1)}V)`}
                        size="small"
                        color={getBatteryColor(device.battery_voltage) as any}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* Device Info */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: device.hive_name ? 'primary.main' : 'text.disabled', mb: 0.5 }}>
                      {device.hive_name ? `Hive: ${device.hive_name}` : 'Unlinked'}
                      {device.location_name && ` (${device.location_name})`}
                    </Typography>
                    {device.hardware_id && (
                      <Typography variant="body2" color="textSecondary">
                        ID: {device.hardware_id}
                      </Typography>
                    )}
                    {(device.last_message_received || device.last_seen) && (
                      <Typography variant="body2" color="textSecondary">
                        Last activity:{" "}
                        {format(parseISO((device.last_message_received || device.last_seen) as string), "PPp")}
                      </Typography>
                    )}
                  </Box>

                  {/* Sensors */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SensorIcon
                      sx={{ fontSize: "1rem", color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {device.sensor_definitions?.length || 0} sensors
                      configured
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditDevice}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Device</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() =>
            selectedDevice && handleMarkForDeletion(selectedDevice.id)
          }
          sx={{
            color: pendingDeletions.includes(selectedDevice?.id || 0)
              ? "warning.main"
              : "error.main",
          }}
        >
          <ListItemIcon>
            <DeleteIcon
              fontSize="small"
              color={
                pendingDeletions.includes(selectedDevice?.id || 0)
                  ? "warning"
                  : "error"
              }
            />
          </ListItemIcon>
          <ListItemText>
            {pendingDeletions.includes(selectedDevice?.id || 0)
              ? "Unmark for Deletion"
              : "Mark for Deletion"}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Device Dialog - Comprehensive BEEP-aligned */}
      <DeviceDialog
        open={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setSelectedDeviceLocal(null);
        }}
        onSave={handleSaveDevice}
        device={selectedDevice}
        apiaries={apiaries}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </Container>
  );
};

export default Devices;
